<template>
    <QLayout view="lHh LpR fFf" class="OrganizationLayout">
        <Sidebar
            v-model="tab"
            :tabs="tabs"
            @update:modelValue="handleChangeTab"
        />

        <QDrawer
            v-if="hasContextDrawer"
            v-model="rightDrawerOpen"
            side="right"
            :overlay="isMobile"
            :width="320"
            class="ContextDrawer q-px-sm"
        >
            <div class="ContextDrawerHeader">
                <div class="flex items-center no-wrap">
                    <q-icon
                        :name="drawerIcon"
                        color="primary"
                        size="20px"
                        class="q-mr-sm"
                    />
                    <div class="ContextDrawerTitle text-primary">
                        {{ drawerTitle }}
                    </div>
                </div>
            </div>

            <q-separator color="primary" />

            <div class="q-pa-md">
                <component
                    :is="contextDrawerPanel.component"
                    v-bind="contextDrawerPanel.props"
                    v-on="contextDrawerPanel.on"
                />
            </div>
        </QDrawer>
        <QHeader elevated class="Header">
            <AdminHeader>
                <template #actions>
                    <div
                        v-if="hasContextDrawer"
                        class="flex row-stretch-center"
                    >
                        <QBtn
                            flat
                            round
                            dense
                            color="primary"
                            icon="view_sidebar"
                            :aria-label="$t('general.filters')"
                            @click="rightDrawerOpen = !rightDrawerOpen"
                        />
                    </div>
                    <HeaderList
                        v-else
                        v-model="filters"
                        :search="selected.search"
                    />
                </template>
            </AdminHeader>
        </QHeader>
        <QPageContainer class="Container">
            <QPage
                class="Screen flex-1"
                :class="{ isDesktop: !isMobile }"
            >
                <RouterView v-slot="{ Component }">
                    <Transition
                        name="main-router"
                        mode="out-in"
                    >
                        <component
                            :is="Component"
                            ref="activeView"
                            :headers="filters"
                            @available-invitations="handleAvailableInvitations"
                        />
                    </Transition>
                </RouterView>
            </QPage>
        </QPageContainer>
    </QLayout>
</template>

<script>
import { AdminHeader, HeaderList, Sidebar } from '@/components'
import { mediaQueryMixin } from '@/mixins'
import {
    OrganizationInvitationsDrawerPanel,
    OrganizationNotificationsDrawerPanel,
    OrganizationProjectsDrawerPanel,
    getOrganizationContextDrawerPanel,
    organizationContextDrawerRoutes,
} from '@/screens/Organization/contextDrawerPanels'

export default {
    name: 'OrganizationLayout',
    components: {
        AdminHeader,
        HeaderList,
        Sidebar,
        OrganizationInvitationsDrawerPanel,
        OrganizationNotificationsDrawerPanel,
        OrganizationProjectsDrawerPanel,
    },
    mixins: [mediaQueryMixin],
    data() {
        return {
            tab: '',
            rightDrawerOpen: true,
            drawerSearch: '',
            projectsSortBy: 'NAME',
            projectsSortDirection: 'ASCENDING',
            notificationsAlreadySeen: false,
            notificationsStartDate: '',
            notificationsEndDate: '',
            availableInvitationsValue: undefined,
            filters: {
                search: '',
                archived: false,
            },
        }
    },
    computed: {
        routeName() {
            return this.$route?.name
        },
        hasContextDrawer() {
            return organizationContextDrawerRoutes.includes(this.routeName)
        },
        contextDrawerPanel() {
            return getOrganizationContextDrawerPanel(this)
        },
        drawerTitle() {
            return this.selected?.label || ''
        },
        drawerIcon() {
            return this.selected?.icon || 'tune'
        },
        projectsSortOptions() {
            return [
                { label: this.$t('general.name'), value: 'NAME' },
                { label: this.$t('general.createdAt'), value: 'CREATED_AT' },
            ]
        },
        sortDirectionOptions() {
            return [
                { label: this.$t('general.ascending'), value: 'ASCENDING' },
                { label: this.$t('general.descending'), value: 'DESCENDING' },
            ]
        },
        availableInvitations() {
            return this.availableInvitationsValue
        },
        selected() {
            return this.tabs.find(({ id }) => id === this.tab) || {}
        },
        tabs() {
            return [
                {
                    id: 'projects',
                    icon: 'cases',
                    label: this.$t('navigation.projects'),
                    screen: 'Projects',
                    search: true,
                },
                {
                    id: 'invitations',
                    icon: 'person_add',
                    label: this.$t('navigation.invitations'),
                    screen: 'Invitations',
                    search: false,
                },
                {
                    id: 'notifications',
                    icon: 'notifications',
                    label: this.$t('navigation.notifications'),
                    screen: 'Notifications',
                    search: true,
                },
            ]
        },
    },
    watch: {
        '$route.name': {
            handler() {
                this.tab = this.getTabName()

                if (!this.hasContextDrawer) return
                this.drawerSearch = this.filters.search || ''

                // Evitar mostrar datos stale entre pantallas
                if (this.routeName !== 'Invitations') this.availableInvitationsValue = undefined

                if (this.routeName === 'Projects') this.syncProjectsSortFromStorage()

                if (this.routeName === 'Notifications') {
                    // Mantener sincronizado el estado con el componente de lista
                    const list = this.$refs.activeView?.$refs?.table
                    if (list && typeof list.activateVisibility === 'boolean') {
                        this.notificationsAlreadySeen = list.activateVisibility
                    }
                }
            },
            immediate: true,
        },
    },
    mounted() {
        this.tab = this.getTabName()

        if (this.isMobile) this.rightDrawerOpen = false
    },
    methods: {
        handleAvailableInvitations(value) {
            this.availableInvitationsValue = typeof value === 'number' ? value : 0
        },
        handleCreate() {
            this.$refs.activeView?.handleNewItem?.()
        },
        clearSearch() {
            this.drawerSearch = ''
            this.applySearch()
        },
        applySearch() {
            this.filters = { ...this.filters, search: this.drawerSearch }
        },
        syncProjectsSortFromStorage() {
            try {
                const sortBy = localStorage.getItem('projectsListSortBy')
                if (sortBy) this.projectsSortBy = sortBy
            } catch (e) { /* ignore */ }

            try {
                const sortDirection = localStorage.getItem('projectsListSortDirection')
                if (sortDirection) this.projectsSortDirection = sortDirection
            } catch (e) { /* ignore */ }
        },
        applyProjectsSort() {
            try { localStorage.setItem('projectsListSortBy', this.projectsSortBy) } catch (e) { /* ignore */ }
            try { localStorage.setItem('projectsListSortDirection', this.projectsSortDirection) } catch (e) { /* ignore */ }

            this.$refs.activeView?.fetchData?.()
        },
        handleNotificationsVisibility(value) {
            this.notificationsAlreadySeen = !!value

            const list = this.$refs.activeView?.$refs?.table
            if (list) list.activateVisibility = this.notificationsAlreadySeen

            this.$refs.activeView?.fetchFilteredData?.({ alreadySeen: this.notificationsAlreadySeen })
        },
        handleNotificationsDates() {
            const list = this.$refs.activeView?.$refs?.table
            if (list?.filters) {
                list.filters.startDate = this.notificationsStartDate
                list.filters.endDate = this.notificationsEndDate
            }
            this.$refs.activeView?.fetchFilteredData?.({
                startDate: this.notificationsStartDate,
                endDate: this.notificationsEndDate,
            })
        },
        handleNotificationsStartDateChange(value) {
            this.notificationsStartDate = value
            this.handleNotificationsDates()
        },
        handleNotificationsEndDateChange(value) {
            this.notificationsEndDate = value
            this.handleNotificationsDates()
        },
        handleChangeTab(value) {
            const tab = this.tabs.find(({ id }) => id === value)
            if (tab && !tab.disabled) this.$router.push({ name: tab.screen }).catch(() => {})
        },
        getTabName() {
            const { name } = this.$route
            return name.toLowerCase()
        },
    },
}
</script>

<style lang="scss">
.ContextDrawer {
    background: $gray-bg;

    .q-field--outlined .q-field__control {
        background: $gray-bg;
    }

    .q-field--outlined:not(.q-field--focused) .q-field__control:before {
        border-color: $gray-04;
        background: $gray-controls;
    }

    .q-btn {
        font-weight: 500;
    }

    .q-btn--outline:before,
    .q-btn-group--outline:before {
        border-width: 2px;
    }
}

.ContextDrawerHeader {
    padding: 0 16px;
    height: $header-height;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ContextDrawerTitle {
    font-weight: 500;
    letter-spacing: 1.25px;
}
</style>
<style scoped lang="scss">
.Header {
    background: $white;
    height: $header-height;
}

.Screen {
    padding: 25px;
    height: 100%;
    overflow: auto;
    width: 100%;

    background: $gray-bg;
}
</style>
